/**
 * The editing canvas's DOM equivalent of `drawBaseChange` (spec 039 D-4,
 * spec 040): how the "to" base of a background change shows at eased
 * progress `p` (0..1, from the frame), over the full-stage layer it sits
 * in. `fade` its opacity; `slide` a translate from the `direction` edge;
 * `zoom` opacity and a centred scale from 0.5; `wipe` a `clip-path: inset`
 * whose edge travels in `direction` (the same rectangle `drawBaseChange`
 * clips to). Any other type (a cut) adds nothing.
 */
import type { CSSProperties } from 'react';
import type { Transition } from '../../../Shared/src/@types';

const pct = (v: number) => `${Math.round(v * 10000) / 100}%`;

export function backgroundChangeStyle(type: Transition['type'], direction: Transition['direction'], p: number): CSSProperties {
  const shown = Math.min(1, Math.max(0, p));
  const hidden = 1 - shown;
  const d = direction || 'left';
  switch (type) {
    case 'fade':
      return { opacity: shown };
    case 'slide': {
      const dx = d === 'left' ? -1 : d === 'right' ? 1 : 0;
      const dy = d === 'up' ? -1 : d === 'down' ? 1 : 0;
      return { transform: `translate(${pct(dx * hidden)}, ${pct(dy * hidden)})` };
    }
    case 'zoom':
      return { opacity: shown, transform: `scale(${0.5 + 0.5 * shown})`, transformOrigin: '50% 50%' };
    case 'wipe': {
      // drawBaseChange's clip: left keeps [W·hidden, W], right [0, W·p], up [H·hidden, H], down [0, H·p].
      const h = pct(hidden);
      const inset = d === 'left' ? `inset(0 0 0 ${h})` : d === 'right' ? `inset(0 ${h} 0 0)` : d === 'up' ? `inset(${h} 0 0 0)` : `inset(0 0 ${h} 0)`;
      return { clipPath: inset };
    }
    default:
      return {};
  }
}
